import React, { useEffect, useRef, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import WaveSurfer from 'wavesurfer.js';
import RegionsPlugin from 'wavesurfer.js/dist/plugin/wavesurfer.regions.min.js';

import {
  clampAudioClipRange,
  createTrimmedWavFile,
  formatAudioTime,
  trimAudioFileToWav,
} from './audioClipUtils.js';
import './AudioMedia.scss';

const AudioClipEditor = ({
  disabled = false,
  file,
  maxDurationSeconds,
  onCancel,
  onConfirm,
  onError,
}) => {
  const waveformRef = useRef(null);
  const waveSurferRef = useRef(null);
  const regionRef = useRef(null);
  const mountedRef = useRef(true);
  const [duration, setDuration] = useState(0);
  const [range, setRange] = useState({ start: 0, end: 0 });
  const [playing, setPlaying] = useState(false);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!file || !waveformRef.current) return undefined;
    const waveSurfer = WaveSurfer.create({
      container: waveformRef.current,
      height: 104,
      responsive: true,
      normalize: true,
      cursorColor: '#176b5b',
      progressColor: '#176b5b',
      waveColor: '#aeb8bd',
      plugins: [RegionsPlugin.create()],
    });

    waveSurfer.on('ready', () => {
      const sourceDuration = waveSurfer.getDuration();
      const initialRange = clampAudioClipRange({
        durationSeconds: sourceDuration,
        maxDurationSeconds,
      });
      const region = waveSurfer.addRegion({
        ...initialRange,
        color: 'rgba(23, 107, 91, 0.18)',
        drag: true,
        resize: true,
      });
      regionRef.current = region;
      setDuration(sourceDuration);
      setRange(initialRange);
    });
    waveSurfer.on('region-updated', (region) => {
      const nextRange = clampAudioClipRange({
        durationSeconds: waveSurfer.getDuration(),
        maxDurationSeconds,
        start: region.start,
        end: region.end,
      });
      if (
        Math.abs(nextRange.start - region.start) > 0.005
        || Math.abs(nextRange.end - region.end) > 0.005
      ) {
        region.update(nextRange);
        return;
      }
      setRange(nextRange);
    });
    waveSurfer.on('pause', () => setPlaying(false));
    waveSurfer.on('finish', () => setPlaying(false));
    waveSurfer.loadBlob(file);
    waveSurferRef.current = waveSurfer;

    return () => {
      regionRef.current = null;
      waveSurferRef.current = null;
      waveSurfer.destroy();
    };
  }, [file, maxDurationSeconds]);

  const togglePlayback = () => {
    const waveSurfer = waveSurferRef.current;
    const region = regionRef.current;
    if (!waveSurfer || !region) return;
    if (playing) {
      waveSurfer.pause();
      return;
    }
    waveSurfer.play(region.start, region.end);
    setPlaying(true);
  };

  const confirm = async () => {
    if (!file || range.end <= range.start) return;
    setProcessing(true);
    try {
      const decodedBuffer = waveSurferRef.current?.backend?.buffer;
      const result = decodedBuffer
        ? createTrimmedWavFile({
          audioBuffer: decodedBuffer,
          fileName: file.name,
          startSeconds: range.start,
          endSeconds: range.end,
        })
        : await trimAudioFileToWav({
          file,
          startSeconds: range.start,
          endSeconds: range.end,
        });
      if (mountedRef.current) onConfirm?.(result);
    } catch (error) {
      if (mountedRef.current) onError?.(error);
    } finally {
      if (mountedRef.current) setProcessing(false);
    }
  };

  const busy = disabled || processing;
  return (
    <div className="audio-clip-editor">
      <div ref={waveformRef} className="audio-clip-editor__waveform" />
      <div className="audio-clip-editor__timeline">
        <span>{formatAudioTime(0)}</span>
        <span>{formatAudioTime(duration)}</span>
      </div>
      <div className="audio-clip-editor__selection">
        <span>起点 <strong>{formatAudioTime(range.start)}</strong></span>
        <span>终点 <strong>{formatAudioTime(range.end)}</strong></span>
        <span>选中 <strong>{formatAudioTime(range.end - range.start)}</strong></span>
        <span>上限 <strong>{formatAudioTime(maxDurationSeconds)}</strong></span>
      </div>
      <div className="audio-clip-editor__actions">
        <Button
          type="button"
          variant="outline-secondary"
          disabled={busy || !duration}
          onClick={togglePlayback}
        >
          <i className={`bi ${playing ? 'bi-pause-fill' : 'bi-play-fill'}`} />
          {playing ? '暂停' : '试听选中片段'}
        </Button>
        <div>
          <Button
            type="button"
            variant="outline-secondary"
            disabled={busy}
            onClick={onCancel}
          >
            取消
          </Button>
          <Button
            type="button"
            disabled={busy || !duration || range.end <= range.start}
            onClick={confirm}
          >
            {processing ? <Spinner size="sm" aria-label="正在处理音频" /> : null}
            <span>使用选中片段</span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AudioClipEditor;
